/** Read and save a native TC lander without touching flow weights. For Media Devoted | Created By Ciro Autiero */
import { useEffect, useState } from 'react';
import { getTcEntity, updateTcLander } from '@/features/flow-optimizer/tracker-entities/entity-api';
import { readTcLander, tcLanderPayload, type TcLanderForm } from '@/features/flow-optimizer/tracker-entities/tres-commas/lander-form';
import { readHttpErrorMessage } from '@/features/flow-optimizer/tabs/standard/v2/paths/path-items/lander-editor/read-http-error';
import type { PathLanderEditDialogProps } from '@/features/flow-optimizer/tabs/standard/v2/paths/path-items/lander-editor';
import { pathItemsSetLanderDisplayName } from '@/features/flow-optimizer/tabs/standard/v2/paths/path-items/path-item-operations';
import { useToast } from '@/providers/ToastProvider';
const errorMessage = (e: unknown) => e instanceof Error && !('response' in e) ? e.message : readHttpErrorMessage(e);
function useLanderLoad(open: boolean, id: string) {
  const [values, setValues] = useState<TcLanderForm | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!open) return;
    let active = true; setValues(null); setError(null);
    void getTcEntity('landers', id).then(readTcLander)
      .then((v) => { if (active) setValues(v); })
      .catch((e: unknown) => { if (active) setError(errorMessage(e)); });
    return () => { active = false; };
  }, [open, id]);
  return { values, setValues, error };
}
async function saveLander(props: PathLanderEditDialogProps, values: TcLanderForm, toast: ReturnType<typeof useToast>) {
  const body = tcLanderPayload(values);
  if (!body.namePostfix || !body.url) throw Error('URL and name postfix are required.');
  await updateTcLander(props.landerId, body);
  pathItemsSetLanderDisplayName(props.onPatch, props.rowIndex, body.namePostfix);
  toast.success('Lander saved', 'Tres Commas lander updated.');
  props.onOpenChange(false);
}
export function useTcLanderEditor(props: PathLanderEditDialogProps) {
  const { values, setValues, error } = useLanderLoad(props.open, props.landerId);
  const [saving, setSaving] = useState(false);
  const toast = useToast();
  const save = async () => {
    if (!values || saving) return;
    setSaving(true);
    try { await saveLander(props, values, toast); }
    catch (e: unknown) { toast.error('Save failed', errorMessage(e)); }
    finally { setSaving(false); }
  };
  const patch = (changes: Partial<TcLanderForm>) => setValues((v) => v ? { ...v, ...changes } : v);
  return { values, error, saving, patch, save };
}
